import { Component, OnInit, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ConfirmationActionComponent } from 'app/components/ui/dialogs/confirmation-action/confirmation-action.component';
import { ConfirmationActionTypes } from 'app/components/ui/dialogs/confirmation-action/confirmation-action.types';
import { generalConsts } from 'app/services/consts/general.consts';
import { UserService } from 'app/services/general/users/user.service';
import { AlertService } from 'app/components/ui/alert/alert.service';
import { finalize } from 'rxjs';

@Component({
    selector: 'change-password-modal',
    templateUrl: './change-password-modal.component.html',
    styleUrls: ['./change-password-modal.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule
    ]
})
export class ChangePasswordModalComponent implements OnInit {
    changePasswordForm: FormGroup;
    loading: boolean = false;
    showCurrentPassword: boolean = false;
    showNewPassword: boolean = false;
    showConfirmPassword: boolean = false;

    confirmationChangePasswordDataObject: ConfirmationActionTypes = {
        title: generalConsts.modules.user.account.dialogs.confirmationChangePasswordDataObject.title,
        type: generalConsts.application.dialogs.type.confirmation,
        closeable: true,
        yesButton: { use: true },
        noButton: { use: true },
        cancelButton: { use: false },
        message: generalConsts.modules.user.account.dialogs.confirmationChangePasswordDataObject.message,
    };

    constructor(
        public dialogRef: MatDialogRef<ChangePasswordModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private _formBuilder: FormBuilder,
        private _matDialog: MatDialog,
        private _userService: UserService,
        private _alertService: AlertService
    ) {}

    ngOnInit(): void {
        this.changePasswordForm = this._formBuilder.group({
            currentPassword: ['', Validators.required],
            newPassword: ['', [Validators.required, Validators.minLength(6)]],
            confirmPassword: ['', Validators.required]
        }, {
            validators: this.passwordMatchValidator
        });
    }

    passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
        const newPassword = group.get('newPassword')?.value;
        const confirmPassword = group.get('confirmPassword')?.value;
        return newPassword === confirmPassword ? null : { passwordMismatch: true };
    }

    savePassword(): void {
        if (this.changePasswordForm.invalid) {
            return;
        }

        // Abre o dialog de confirmação
        const confirmationDialogRef = this._matDialog.open(ConfirmationActionComponent, {
            autoFocus: false,
            data: {
                data: this.confirmationChangePasswordDataObject,
            },
            disableClose: true,
            width: '95%',
            maxWidth: '500px',
        });

        confirmationDialogRef.afterClosed().subscribe(result => {
            if (result && result.action === generalConsts.modules.user.account.dialogs.actions.yes) {
                this.processPasswordChange();
            }
        });
    }

    processPasswordChange(): void {
        this.loading = true;

        const currentPassword = this.changePasswordForm.get('currentPassword')?.value;
        const newPassword = this.changePasswordForm.get('newPassword')?.value;

        this._userService.changePassword(currentPassword, newPassword)
            .pipe(finalize(() => this.loading = false))
            .subscribe({
                next: (response) => {
                    this._alertService.success(response?.message, {autoClose: true});
                    this.dialogRef.close({ action: 'saved' });
                },
                error: (error) => {
                    console.error('Erro ao alterar senha:', error);
                    this._alertService.error(error?.error, {autoClose: true});
                }
            });
    }

    closeModal(): void {
        this.dialogRef.close();
    }

    togglePasswordVisibility(field: string): void {
        if (field === 'current') {
            this.showCurrentPassword = !this.showCurrentPassword;
        } else if (field === 'new') {
            this.showNewPassword = !this.showNewPassword;
        } else if (field === 'confirm') {
            this.showConfirmPassword = !this.showConfirmPassword;
        }
    }
}
